// Revoked or suspended: the state, the count of what is still on the
// device, and the promise that nothing is discarded (D-62, D-70).
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Navigate, useNavigate } from 'react-router';
import { brandOf } from '../app/brand';
import { useRuntime, useSession } from '../app/hooks';
import { useLiveQuery } from '../app/useLiveQuery';
import { useTitleSection } from '../app/documentTitle';
import { pendingCount } from '../db/database';
import { BrandLockup } from '../ui/BrandLockup';

export function StatusScreen() {
  const { t } = useTranslation();
  const { session, db, engine } = useRuntime();
  const snapshot = useSession();
  const brand = brandOf(snapshot);
  const navigate = useNavigate();
  const pending = useLiveQuery(() => pendingCount(db), 0, [db]);
  const [checking, setChecking] = useState(false);
  // INC-14a-3a-1: the tab names this screen before the organization, and no product.
  useTitleSection(
    snapshot.status === 'revoked'
      ? t('status.revokedTitle')
      : snapshot.status === 'suspended'
        ? t('status.suspendedTitle')
        : null,
    'outside',
  );

  if (snapshot.status !== 'revoked' && snapshot.status !== 'suspended') {
    return <Navigate to="/" replace />;
  }
  const revoked = snapshot.status === 'revoked';

  const signInAgain = async (): Promise<void> => {
    await session.acknowledgeState();
    await navigate('/login');
  };

  const checkAgain = async (): Promise<void> => {
    setChecking(true);
    try {
      const token = await session.refresh();
      if (token !== null) {
        await db.session.delete('state');
        await session.loadProfile();
        engine.trigger('manual');
        await navigate('/');
      }
    } finally {
      setChecking(false);
    }
  };

  return (
    <div className="access" data-testid="status-screen" data-state={snapshot.status}>
      {/* INC-12a (A.4): the organization above, as on the access screen. */}
      <BrandLockup
        name={brand.name === '' ? t('access.neutralTitle') : brand.name}
        logoUrl={brand.logoUrl}
        markUrl={brand.markUrl}
      />
      <div className="card stack">
        <h1 style={{ margin: 0 }}>
          {revoked ? t('status.revokedTitle') : t('status.suspendedTitle')}
        </h1>
        <p>{revoked ? t('status.revokedBody') : t('status.suspendedBody')}</p>
        <div className="alert info" data-testid="status-pending">
          <div>{t('status.pendingCount', { count: pending })}</div>
          <div className="muted">{t('status.pendingKept')}</div>
        </div>
        {revoked ? (
          <button type="button" className="btn block" onClick={() => void signInAgain()}>
            {t('status.signInAgain')}
          </button>
        ) : (
          <>
            <button
              type="button"
              className="btn block"
              disabled={checking}
              onClick={() => void checkAgain()}
            >
              {t('status.checkAgain')}
            </button>
            <button
              type="button"
              className="btn block secondary"
              onClick={() => void signInAgain()}
            >
              {t('status.signInAgain')}
            </button>
          </>
        )}
      </div>
    </div>
  );
}
